import type {
  BaseLeagueFilters,
  League,
  LeagueProjectionAttributes,
  LeagueProjectionFilters,
  LeaguesResponse
} from '@library/types/leagues';
import {
  formatToLeagueProjectionAttributes,
  getLeagueProjectionFilters,
  rankThenNameSort
} from '@bo-utils/leagueProjectionFilters';
import { LEAGUES_WITH_DURATIONS } from '@bo-constants/leagues';

import { getDurationsWithRelatedIds } from '@bo-utils/leagues';

export const hasStandAloneOwnAttributes = ({ name }: { name?: string }) => {
  return Object.values(LEAGUES_WITH_DURATIONS).includes(name ?? '');
};

const getLeagueFilterAttributes = ({
  isStandAloneLeague,
  leagueData,
  projectionFilters
}: {
  isStandAloneLeague: boolean;
  leagueData: LeaguesResponse;
  projectionFilters: LeagueProjectionFilters;
}) => {
  const leagueProjectionFilters = getLeagueProjectionFilters({
    leagueData,
    projectionFilters
  });

  const durations = leagueProjectionFilters.filter(
    (leagueProjectionFilter) =>
      leagueProjectionFilter.leagueProjectionFilterType === 'Duration'
  );

  const durationsWithRelations = isStandAloneLeague
    ? getDurationsWithRelatedIds({ durations })
    : durations;

  const statTypes = leagueProjectionFilters.filter(
    (leagueProjectionFilter) =>
      leagueProjectionFilter.leagueProjectionFilterType === 'StatType'
  );

  const positions = leagueProjectionFilters.filter(
    (leagueProjectionFilter) =>
      leagueProjectionFilter.leagueProjectionFilterType === 'Position'
  );

  const projectionTypes = leagueProjectionFilters.filter(
    (leagueProjectionFilter) =>
      leagueProjectionFilter.leagueProjectionFilterType === 'ProjectionType'
  );

  return {
    durations: durationsWithRelations,
    projectionTypes: rankThenNameSort({
      leagueProjectionAttributesList: projectionTypes
    }),
    positions: rankThenNameSort({ leagueProjectionAttributesList: positions }),
    statTypes: rankThenNameSort({ leagueProjectionAttributesList: statTypes })
  };
};

/* Call this getLeagueDurations API to get durations for only base leagues (not sub leagues that are not part of the leagues drop down) */
export const getLeagueAttributes = ({
  leagueData,
  selectedLeagueId
}: {
  leagueData: LeaguesResponse;
  selectedLeagueId: League['id'];
}): Array<BaseLeagueFilters> => {
  const { data, included } = leagueData;
  const selectedLeague = data.find((league) => league.id === selectedLeagueId);

  if (!selectedLeague) {
    return [];
  }

  const {
    attributes: { name },
    relationships: { projection_filters: projectionFilters }
  } = selectedLeague;

  /* If league like MLBLIVE return all the league attributes from league relationships */
  const isStandAloneLeague = hasStandAloneOwnAttributes({ name });

  const parentLeague = {
    leagueId: selectedLeagueId,
    name,
    ...getLeagueFilterAttributes({
      isStandAloneLeague,
      leagueData,
      projectionFilters
    })
  };

  if (isStandAloneLeague) {
    return [parentLeague];
  }

  const fullDurationFilter = included.find(
    ({ attributes: { name: durationName, type } }) =>
      type === 'Duration' && durationName === 'Full'
  );

  const fullDuration = fullDurationFilter
    ? formatToLeagueProjectionAttributes({
        projectionFilter: fullDurationFilter
      })
    : undefined;

  const parentLeagueAttributes = {
    ...parentLeague,
    durations: fullDuration ? [fullDuration] : []
  };

  const subLeagues = data.filter(
    (league) => Number(selectedLeagueId) === league.attributes.parent_id
  );

  /* If no sub leagues return Full Duration and parent league attributes */
  if (!subLeagues.length) {
    return [parentLeagueAttributes];
  }

  /* Link all the sub leagues attributes */
  const subLeaguesAttributes = subLeagues.map((subLeague) => {
    const {
      id,
      attributes: { name: subLeagueName },
      relationships: { projection_filters: subLeagueProjectionFilters }
    } = subLeague;

    return {
      ...getLeagueFilterAttributes({
        isStandAloneLeague,
        leagueData,
        projectionFilters: subLeagueProjectionFilters
      }),
      leagueId: id,
      name: subLeagueName
    };
  });

  return [parentLeagueAttributes, ...subLeaguesAttributes];
};

export const getFullDuration = ({
  durations
}: {
  durations: Array<LeagueProjectionAttributes>;
}): LeagueProjectionAttributes | undefined => {
  return durations.find(
    (duration) => duration.leagueProjectionFilterName === 'Full'
  );
};

export const getFirstInnDuration = ({
  durations
}: {
  durations: Array<LeagueProjectionAttributes>;
}): LeagueProjectionAttributes | undefined => {
  const fullDuration = getFullDuration({ durations });

  if (fullDuration) {
    return fullDuration;
  }

  return durations.find(
    (duration) => duration.leagueProjectionFilterName === '1st Inning'
  );
};

export const getMainDuration = ({
  durations,
  leagueName
}: {
  durations: Array<LeagueProjectionAttributes>;
  leagueName: string;
}): LeagueProjectionAttributes | undefined => {
  if (!leagueName || !durations.length) {
    return undefined;
  }

  return hasStandAloneOwnAttributes({ name: leagueName })
    ? getFirstInnDuration({ durations })
    : getFullDuration({ durations });
};
